import React, { useState, useRef, useEffect } from 'react';
import { FileText, Download, ArrowLeft, X, RotateCcw, Trash2, Mail } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';
import { useUserRole } from '../hooks/useUserRole';

interface NDAPageProps {
  onBack?: () => void;
  dealId?: string;
  dealTitle?: string;
  recipientEmail?: string;
  isRecipientView?: boolean;
}

export const NDAPage: React.FC<NDAPageProps> = ({ 
  onBack, 
  dealId, 
  dealTitle, 
  recipientEmail
}) => {
  const { user, isLoaded } = useUser();
  const { userProfile, isLoading: isUserProfileLoading } = useUserRole();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [buyerSignature, setBuyerSignature] = useState<string | null>(null);
  const [recipientSignature, setRecipientSignature] = useState<string | null>(null);
  const [recipientDetails, setRecipientDetails] = useState({
    name: '',
    company: '',
    email: recipientEmail || user?.emailAddresses?.[0]?.emailAddress || '',
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [businessName, setBusinessName] = useState<string>('');
  const [currentDealId, setCurrentDealId] = useState<string>(dealId || '');
  const [isLoading, setIsLoading] = useState(true);
  const [isViewOnly, setIsViewOnly] = useState(false);
  
  const buyerSignatureRef = useRef<SignatureCanvas>(null);
  const recipientSignatureRef = useRef<SignatureCanvas>(null);

  // Load deal information from URL parameters
  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const urlDealId = currentUrl.searchParams.get('deal_id');
    const urlEmail = currentUrl.searchParams.get('email');
    const urlDealTitle = currentUrl.searchParams.get('deal_title');
    
    console.log('[DEBUG - DELETE WHEN FINISHED] NDAPage URL params:', { urlDealId, urlEmail, urlDealTitle, dealTitle }); // [DEBUG - DELETE WHEN FINISHED]
    
    // Set email from URL if provided
    if (urlEmail && !recipientDetails.email) {
      setRecipientDetails(prev => ({
        ...prev,
        email: urlEmail
      }));
    }
    
    // Set dealId from URL if provided
    if (urlDealId && !currentDealId) {
      setCurrentDealId(urlDealId);
    }
    
    // Check if this is view-only mode
    const viewOnly = currentUrl.searchParams.get('view_only');
    if (viewOnly === 'true') {
      setIsViewOnly(true);
    }
    
    // Set business name from URL parameter first (highest priority)
    if (urlDealTitle) {
      console.log('[DEBUG - DELETE WHEN FINISHED] Setting business name from URL:', decodeURIComponent(urlDealTitle)); // [DEBUG - DELETE WHEN FINISHED]
      setBusinessName(decodeURIComponent(urlDealTitle));
    }
    // Then check if dealTitle prop was provided
    else if (dealTitle) {
      console.log('[DEBUG - DELETE WHEN FINISHED] Setting business name from prop:', dealTitle); // [DEBUG - DELETE WHEN FINISHED]
      setBusinessName(dealTitle);
    }
    // Finally, fetch from backend API as fallback (for backward compatibility)
    else if (urlDealId && urlEmail) {
      console.log('[DEBUG - DELETE WHEN FINISHED] Fetching business name from backend API'); // [DEBUG - DELETE WHEN FINISHED]
      const fetchDealInfo = async () => {
        try {
          const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
          const response = await fetch(`${backendUrl}/check-pending-nda/${encodeURIComponent(urlEmail)}`);
          const data = await response.json();
          console.log('[DEBUG - DELETE WHEN FINISHED] Backend API response:', data); // [DEBUG - DELETE WHEN FINISHED]
          if (data.has_pending_nda && data.public_business_alias) {
            console.log('[DEBUG - DELETE WHEN FINISHED] Setting business name from API:', data.public_business_alias); // [DEBUG - DELETE WHEN FINISHED]
            setBusinessName(data.public_business_alias);
          }
        } catch (error) {
          console.error('Error fetching deal info:', error);
        }
      };
      fetchDealInfo();
    }
  }, []);

  // Check authentication and handle redirects
  useEffect(() => {
    if (!isLoaded) return;

    if (!user) {
      // User is not authenticated, redirect to signup/login
      const currentUrl = new URL(window.location.href);
      const dealId = currentUrl.searchParams.get('deal_id');
      const email = currentUrl.searchParams.get('email');
      const recipient = currentUrl.searchParams.get('recipient');
      
      // Build redirect URL to come back to NDA after authentication
      const redirectUrl = `/nda?deal_id=${dealId}&email=${email}&recipient=${recipient}`;
      window.location.href = `/sign-up?redirect_to=${encodeURIComponent(redirectUrl)}`;
      return;
    }

    // User is authenticated, continue with NDA
    setIsLoading(false);
  }, [user, isLoaded]);

  // Load Lee Stauss's signature automatically and set user email
  useEffect(() => {
    if (!user) return; // Only load signature if user is authenticated
    
    // Set user email if not already set
    if (user.emailAddresses?.[0]?.emailAddress && !recipientDetails.email) {
      setRecipientDetails(prev => ({
        ...prev,
        email: user.emailAddresses[0].emailAddress
      }));
    }
    
    // Pre-load Lee Stauss's signature from the sig.png file
    const loadSignature = async () => {
      try {
        // Try to load the signature image from the public folder
        const response = await fetch('/sig.png');
        if (response.ok) {
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onload = () => {
            setBuyerSignature(reader.result as string);
          };
          reader.readAsDataURL(blob);
        }
      } catch (error) {
      }
    };
    
    loadSignature();
  }, [user]);

  // Fetch business name from deal scenarios
  useEffect(() => {
    const fetchBusinessName = async () => {
      if (!dealId) return;
      
      try {
        // Get the first scenario to extract business name
        const scenarios = await DatabaseService.listDealScenarios(dealId, 'system'); // Use system user for public access
        if (scenarios.data && scenarios.data.length > 0) {
          const firstScenario = scenarios.data[0];
          const businessNameFromScenario = firstScenario.scenario_data?.business_name;
          if (businessNameFromScenario) {
            setBusinessName(businessNameFromScenario);
          }
        }
      } catch (error) {
        console.error('Error fetching business name:', error);
        // Fallback to deal title if business name not found
        setBusinessName(dealTitle || 'Business Opportunity');
      }
    };

    fetchBusinessName();
  }, [dealId, dealTitle]);

  // Load executed NDA data when deal ID is available
  useEffect(() => {
    if (currentDealId && user?.emailAddresses?.[0]?.emailAddress) {
      const loadExecutedNDA = async () => {
        try {
          // Get the user's email for authentication
          const userEmail = user?.emailAddresses?.[0]?.emailAddress;
          if (!userEmail) {
            console.error('No user email found');
            return;
          }
          
          const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
          const response = await fetch(`${backendUrl}/nda-document-info/${currentDealId}`, {
            headers: {
              'user-email': userEmail
            }
          });
          const data = await response.json();
          
          if (data.has_nda) {
            
            // Set view-only mode since NDA is already executed
            setIsViewOnly(true);
            
            // Set the recipient details from the executed NDA
            setRecipientDetails({
              name: data.recipient_name || '',
              company: data.recipient_company || '',
              email: data.recipient_email || '',
              address: data.recipient_address || ''
            });
            
            // Set the signatures
            if (data.buyer_signature) {
              setBuyerSignature(data.buyer_signature);
            }
            if (data.recipient_signature) {
              setRecipientSignature(data.recipient_signature);
            }
          }
        } catch (error) {
          console.error('Error loading executed NDA:', error);
        }
      };
      
      loadExecutedNDA();
    }
  }, [currentDealId, user?.emailAddresses?.[0]?.emailAddress]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      // Show loading state
      setSubmitMessage('Generating PDF...');
      
      // Get the current page's HTML content (already rendered)
      let pageContent = document.documentElement.outerHTML;
      
      // Add CSS that works reliably with WeasyPrint
      const tailwindCSS = `
        <style>
          /* Reset and base styles */
          * { box-sizing: border-box; }
          body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
          
          /* Use CSS Grid for reliable two-column layout */
          .flex.items-start.gap-6 {
            display: grid !important;
            grid-template-columns: 1fr 200px !important;
            gap: 1.5rem !important;
            width: 100% !important;
            margin-bottom: 2rem !important;
            align-items: start !important;
          }
          
          .flex-1 {
            grid-column: 1 !important;
            width: 100% !important;
          }
          
          .flex-shrink-0 {
            grid-column: 2 !important;
            width: 200px !important;
            text-align: center !important;
          }
          
          /* Typography - make headings stand out */
          .text-2xl {
            font-size: 1.8rem !important;
            line-height: 2.2rem !important;
            font-weight: 700 !important;
            color: #111827 !important;
            margin-bottom: 0.5rem !important;
            text-align: center !important;
          }
          
          .text-xl {
            font-size: 1.4rem !important;
            line-height: 1.8rem !important;
            font-weight: 600 !important;
            color: #1f2937 !important;
            margin-bottom: 1.5rem !important;
            text-align: center !important;
          }
          
          .text-gray-700 {
            color: #374151 !important;
            line-height: 1.6 !important;
            margin-bottom: 1rem !important;
            font-size: 1rem !important;
          }
          
          /* Image styling */
          .w-64.h-64 {
            width: 14rem !important;
            height: 14rem !important;
            object-fit: cover !important;
            border-radius: 0.5rem !important;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
            display: block !important;
            margin: 0 auto !important;
          }
          
          /* Input field styling - keep inline */
          input[type="text"] {
            border-bottom: 2px solid #9ca3af !important;
            padding: 0.3rem 0.5rem !important;
            color: #374151 !important;
            background-color: #fef08a !important;
            margin: 0 0.3rem !important;
            width: 12rem !important;
            display: inline !important;
            font-size: 1rem !important;
          }
          
          /* Spacing utilities */
          .space-y-4 > * + * {
            margin-top: 1rem !important;
          }
          
          .space-y-6 > * + * {
            margin-top: 1.5rem !important;
          }
          
          .mb-2 { margin-bottom: 0.5rem !important; }
          .mb-4 { margin-bottom: 1rem !important; }
          .mb-6 { margin-bottom: 1.5rem !important; }
          
          /* Container styling */
          .max-w-4xl {
            max-width: 100% !important;
            margin: 0 auto !important;
          }
          
          .p-8 {
            padding: 2rem !important;
          }
          
          /* Fallback for flexbox if table doesn't work */
          .flex {
            display: flex !important;
          }
          
          .items-start {
            align-items: flex-start !important;
          }
          
          .gap-6 {
            gap: 1.5rem !important;
          }
        </style>
      `;
      
      // Insert the CSS into the HTML content
      pageContent = pageContent.replace('<head>', `<head>${tailwindCSS}`);
      
      // Remove elements that shouldn't be in the PDF
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = pageContent;
      
      // Remove the navigation header - target the specific class
      let headerRemoved = false;
      
      // Approach 1: Target the exact navigation header class
      const headerElement = tempDiv.querySelector('div.bg-white.shadow-sm.border-b.border-gray-100');
      if (headerElement) {
        headerElement.remove();
        headerRemoved = true;
      }
      
      // Approach 2: Look for any div with the header classes (fallback)
      const headerElements = tempDiv.querySelectorAll('div.bg-white.shadow-sm.border-b');
      headerElements.forEach(element => {
        element.remove();
        headerRemoved = true;
      });
      
      // Approach 3: Remove any elements with print:hidden class
      const printHiddenElements = tempDiv.querySelectorAll('[class*="print:hidden"]');
      printHiddenElements.forEach(element => {
        element.remove();
        headerRemoved = true;
      });
      
      // Get the cleaned HTML content
      pageContent = tempDiv.innerHTML;
      
      // Get deal ID and email from various sources
      const currentUrl = new URL(window.location.href);
      const urlDealId = currentUrl.searchParams.get('deal_id');
      const urlEmail = currentUrl.searchParams.get('email');
      
      // Use component props, state, or URL parameters
      const finalDealId = dealId || currentDealId || urlDealId;
      const finalEmail = recipientDetails.email || urlEmail || user?.emailAddresses?.[0]?.emailAddress;
      
      if (!finalDealId || !finalEmail) {
        alert(`Missing information - Deal ID: ${finalDealId || 'missing'}, Email: ${finalEmail || 'missing'}`);
        return;
      }

      // Send the current page content to backend for PDF generation
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/generate-nda-pdf-from-html`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          html_content: pageContent,
          deal_id: finalDealId,
          email: finalEmail,
          business_name: businessName || dealTitle || 'Document'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      // Get the PDF blob
      const pdfBlob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NDA_${businessName || dealTitle || 'Document'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSubmitMessage('PDF downloaded successfully!');
      setTimeout(() => setSubmitMessage(''), 3000);
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setSubmitMessage('Error generating PDF. Please try again.');
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  const openImageModal = () => setIsImageModalOpen(true);
  const closeImageModal = () => setIsImageModalOpen(false);

  const clearSignature = (ref: React.RefObject<SignatureCanvas>) => {
    if (ref.current) {
      ref.current.clear();
    }
  };

  const saveSignature = (ref: React.RefObject<SignatureCanvas>, setSignature: (sig: string) => void) => {
    if (ref.current && !ref.current.isEmpty()) {
      const signatureData = ref.current.toDataURL();
      setSignature(signatureData);
    }
  };

  const handleRecipientDetailsChange = (field: string, value: string) => {
    setRecipientDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitNDA = async () => {
    
    if (!recipientDetails.company || !recipientDetails.email) {
      setSubmitMessage('Please fill in all required recipient details');
      return;
    }

    if (!buyerSignature || !recipientSignature) {
      setSubmitMessage('Both signatures are required');
      return;
    }

    // Wait for user profile to be loaded
    if (isUserProfileLoading || !userProfile) {
      setSubmitMessage('Please wait while we verify your account...');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Get the current page's HTML content (same as download button)
      let pageContent = document.documentElement.outerHTML;
      
      // Add CSS that works reliably with WeasyPrint (same as download button)
      const tailwindCSS = `
        <style>
          /* Reset and base styles */
          * { box-sizing: border-box; }
          body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
          
          /* Use CSS Grid for reliable two-column layout */
          .flex.items-start.gap-6 {
            display: grid !important;
            grid-template-columns: 1fr 200px !important;
            gap: 1.5rem !important;
            width: 100% !important;
            margin-bottom: 2rem !important;
            align-items: start !important;
          }
          
          .flex-1 {
            grid-column: 1 !important;
            width: 100% !important;
          }
          
          .flex-shrink-0 {
            grid-column: 2 !important;
            width: 200px !important;
            text-align: center !important;
          }
          
          /* Typography - make headings stand out */
          .text-2xl {
            font-size: 1.8rem !important;
            line-height: 2.2rem !important;
            font-weight: 700 !important;
            color: #111827 !important;
            margin-bottom: 0.5rem !important;
            text-align: center !important;
          }
          
          .text-xl {
            font-size: 1.4rem !important;
            line-height: 1.8rem !important;
            font-weight: 600 !important;
            color: #1f2937 !important;
            margin-bottom: 1.5rem !important;
            text-align: center !important;
          }
          
          .text-gray-700 {
            color: #374151 !important;
            line-height: 1.6 !important;
            margin-bottom: 1rem !important;
            font-size: 1rem !important;
          }
          
          /* Image styling */
          .w-64.h-64 {
            width: 14rem !important;
            height: 14rem !important;
            object-fit: cover !important;
            border-radius: 0.5rem !important;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
            display: block !important;
            margin: 0 auto !important;
          }
          
          /* Input field styling - keep inline */
          input[type="text"] {
            border-bottom: 2px solid #9ca3af !important;
            padding: 0.3rem 0.5rem !important;
            color: #374151 !important;
            background-color: #fef08a !important;
            margin: 0 0.3rem !important;
            width: 12rem !important;
            display: inline !important;
            font-size: 1rem !important;
          }
          
          /* Spacing utilities */
          .space-y-4 > * + * {
            margin-top: 1rem !important;
          }
          
          .space-y-6 > * + * {
            margin-top: 1.5rem !important;
          }
          
          .mb-2 { margin-bottom: 0.5rem !important; }
          .mb-4 { margin-bottom: 1rem !important; }
          .mb-6 { margin-bottom: 1.5rem !important; }
          
          /* Container styling */
          .max-w-4xl {
            max-width: 100% !important;
            margin: 0 auto !important;
          }
          
          .p-8 {
            padding: 2rem !important;
          }
          
          /* Fallback for flexbox if table doesn't work */
          .flex {
            display: flex !important;
          }
          
          .items-start {
            align-items: flex-start !important;
          }
          
          .gap-6 {
            gap: 1.5rem !important;
          }
        </style>
      `;
      
      // Insert the CSS into the HTML content
      pageContent = pageContent.replace('<head>', `<head>${tailwindCSS}`);
      
      // Remove elements that shouldn't be in the PDF
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = pageContent;
      
      // Remove the navigation header - target the specific class
      let headerRemoved = false;
      
      // Approach 1: Target the exact navigation header class
      const headerElement = tempDiv.querySelector('div.bg-white.shadow-sm.border-b.border-gray-100');
      if (headerElement) {
        headerElement.remove();
        headerRemoved = true;
      }
      
      // Approach 2: Look for any div with the header classes (fallback)
      const headerElements = tempDiv.querySelectorAll('div.bg-white.shadow-sm.border-b');
      headerElements.forEach(element => {
        element.remove();
        headerRemoved = true;
      });
      
      // Approach 3: Remove any elements with print:hidden class
      const printHiddenElements = tempDiv.querySelectorAll('[class*="print:hidden"]');
      printHiddenElements.forEach(element => {
        element.remove();
        headerRemoved = true;
      });
      
      // Get the cleaned HTML content
      pageContent = tempDiv.innerHTML;
      
      // Prepare form data with user authentication
      const formData = new URLSearchParams({
        deal_id: currentDealId || dealId || '',
        recipient_email: recipientDetails.email,
        recipient_name: recipientDetails.name,
        recipient_company: recipientDetails.company,
        recipient_address: recipientDetails.address,
        buyer_signature: buyerSignature || '',
        recipient_signature: recipientSignature || '',
        invitation_id: new URLSearchParams(window.location.search).get('invitation_id') || '',
        // Add user authentication data
        clerk_user_id: user?.id || '',
        supabase_user_id: userProfile.id || '',
        user_email: user?.emailAddresses?.[0]?.emailAddress || '',
        // Add the actual HTML content for PDF generation
        html_content: pageContent
      });
      
      // Submit NDA to backend
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/submit-nda`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setSubmitMessage('NDA submitted successfully! Redirecting to deal...');
        
        // Redirect to main app - user is already authenticated and now has deal access
        const appUrl = `/?deal_id=${currentDealId}&nda_completed=true`;
        setTimeout(() => {
          window.location.href = appUrl;
        }, 2000);
      } else {
        throw new Error(result.message || 'Failed to submit NDA');
      }
      
    } catch (error) {
      console.error('Error submitting NDA:', error);
      setSubmitMessage('Error submitting NDA. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  // Show loading state while checking authentication
  if (!isLoaded || isLoading || isUserProfileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading NDA...</p>
        </div>
      </div>
    );
  }

  // Don't render NDA if user is not authenticated (should have been redirected)
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header - Only visible on screen, not in print */}
      <div className="bg-white shadow-sm border-b border-gray-100 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Dashboard
                </button>
              )}
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Non-Disclosure Agreement</h1>
                  <p className="text-sm text-gray-600">
                    {businessName ? `For: ${businessName}` : (dealTitle ? `For: ${dealTitle}` : 'Business Acquisition Document')}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FileText className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* NDA Document - This will be printed */}
      <div className="max-w-4xl mx-auto bg-white print:shadow-none print:max-w-none print:mx-0 print:bg-white">
        <div className="p-8 print:p-12">
          {/* Main Content */}
          <div className="space-y-6">
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">CONFIDENTIALITY/NON-DISCLOSURE AGREEMENT</h1>
                <h2 className="text-xl font-semibold text-gray-800 mb-6">POTENTIAL MERGER OR ACQUISITION</h2>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  This CONFIDENTIALITY AGREEMENT (the "Agreement") is by and between <strong>SNQ AFFILIATES LLC</strong>, 2501 NE 23rd St, Suite A445, Oklahoma City, OK, 73111 (hereinafter "Disclosing Party") and 
                  <input 
                    type="text" 
                    value={recipientDetails.company}
                    onChange={isViewOnly ? undefined : (e) => handleRecipientDetailsChange('company', e.target.value)}
                    readOnly={isViewOnly}
                    className={`border-b border-gray-400 px-2 py-1 text-gray-700 mx-2 w-64 ${isViewOnly ? 'bg-gray-100' : 'bg-yellow-200 focus:outline-none focus:border-blue-500'}`}
                    placeholder={isViewOnly ? "" : "Enter recipient company name"}
                  />
                  (hereinafter "Recipient").
                </p>
              </div>
              <img 
                src="https://qtpjctvowxyhowiywzsn.supabase.co/storage/v1/object/public/images/family.jpeg" 
                alt="Lee Stauss" 
                className="w-64 h-64 object-cover rounded-lg shadow-sm flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={openImageModal}
              />
            </div>

            {/* WHEREAS clauses */}
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>WHEREAS</strong>, Recipient has requested information from the Disclosing Party in connection with the consideration of a possible merger or acquisition of {businessName || dealTitle || 'Target Company'} ("Target Company") being evaluated by Disclosing Party ("Transaction").
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>WHEREAS</strong>, in the course of consideration of the possible Transaction involving Target Company, Disclosing Party may disclose to Recipient confidential, important and/or proprietary trade secret information concerning Target Company and its activities, as well as information related to the Transaction.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>THEREFORE</strong>, the parties agree to enter into a confidentiality relationship with respect to the Disclosing Party to Recipient of certain information.
              </p>
            </div>

            {/* Section 1: Definitions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">1. <strong>Definitions.</strong></h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                For purposes of this Agreement, "Confidential Information" shall include all information or material relating to Target Company, the Transaction, or otherwise disclosed by Disclosing Party that has or could have commercial value or other utility in the business or prospective business of Target Company or in connection with the Transaction. Confidential Information also includes all information of which unauthorized disclosure could be detrimental to the interests of Disclosing Party, Target Company, or the Transaction, whether or not Disclosing Party identifies such information as Confidential Information. By example and without limitation, Confidential Information includes, but is not limited to, Target Company's financial statements, business plan and plan of operations, customer lists, vendor information, employee information, operational data, and all information related to the Transaction terms and negotiations.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                For Purposes of this Agreement, the term "Recipient" shall include Recipient, the company he or she represents, and any and all intermediaries (hereinafter "Intermediary") representing Recipient. For purposes of this Agreement, the term "Representative" shall include Recipient's directors, officers, employees, agents, and financial, legal and other advisors.
              </p>
            </div>

            {/* Section 2: Exclusions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">2. <strong>Exclusions.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                Confidential Information does not include information that Recipient can demonstrate: (a) was in Recipient's possession prior to its being furnished to Recipient under terms of this Agreement, provided the source of that information was not known by Recipient to be bound by confidentiality agreement with or other continual, legal or fiduciary obligation of confidentiality to Disclosing Party; (b) is now, or hereafter becomes, through no act or failure to act on the part of the recipient, generally known to the public; (c) is rightfully obtained by Recipient from a third party, without breach of any obligation to Disclosing Party; or (d) is independently developed by Recipient without use of or reference to the Confidential Information.
              </p>
            </div>

            {/* Section 3: Confidentiality */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">3. <strong>Confidentiality.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                Recipient and its Representatives shall not disclose any of the Confidential Information in any manner whatsoever, except as provided in paragraphs 4 and 5 of this Agreement, and shall hold and maintain the Confidential Information in strictest confidence. Recipient hereby agrees to indemnify Disclosing Party against any and all losses, damages, claims, expenses, and attorneys' fees incurred or suffered by Disclosing Party as a result of breach of this Agreement by Recipient or its Representatives.
              </p>
            </div>

            {/* Section 4: Permitted Disclosures */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">4. <strong>Permitted Disclosures.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                Recipient may disclose Disclosing Party's Confidential Information to Recipient's responsible Representatives with a bona fide need to know such Confidential Information, but only to the extent necessary to provide advisory services to Disclosing Party regarding the Transaction and only if such Representatives are advised of the confidential nature of such Confidential Information and the terms of this Agreement and are bound by a written agreement or by a legally enforceable code of professional responsibility to protect the confidentiality of such Confidential Information.
              </p>
            </div>

            {/* Section 5: Required Disclosures */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">5. <strong>Required Disclosures.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                Recipient may disclose Disclosing Party's Confidential Information if and to the extent that such disclosure is required by court order, provided the Recipient provides Disclosing Party a reasonable opportunity to review the disclosure before it is made and to interpose its own objection to the disclosure.
              </p>
            </div>

            {/* Section 6: Use */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">6. <strong>Use.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                Recipient and its Representatives shall use the Confidential Information solely for the purpose of providing advisory services to Disclosing Party regarding the Transaction involving Target Company and shall not in any way use the Confidential Information to the detriment of Disclosing Party, Target Company, or the Transaction. Nothing in the Agreement shall be construed as granting any rights to Recipient, by license or otherwise, to any of Disclosing Party's Confidential Information.
              </p>
            </div>

            {/* Section 7: Return or Destruction of Documents */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">7. <strong>Return or Destruction of Documents.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                Upon completion of Recipient's advisory services or at any time upon the request of Disclosing Party for any reason, Recipient shall destroy or return to Disclosing Party any and all record, notes, and other written, printed or other tangible materials in its possession pertaining to the Confidential Information immediately on the written request of Disclosing Party. The destruction or returning of materials shall not relieve Recipient from compliance with other terms and conditions of the Agreement.
              </p>
            </div>

            {/* Section 8: No Additional Agreements */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">8. <strong>No Additional Agreements.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                Neither the holding of discussions nor the exchange of material or information shall be constructed as an obligation of Disclosing Party to enter into any other agreement with Recipient or prohibit Disclosing Party from providing the same or similar information to other parties and entering into agreements with other parties. Disclosing Party reserves the right, in its sole discretion, to reject any and all proposals made by Recipient or its Representatives. Additional agreements of the parties, if any, shall be in writing signed by Disclosing party and Recipient.
              </p>
            </div>

            {/* Section 9: Irreparable Harm */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">9. <strong>Irreparable Harm.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                Recipient understands and acknowledges that any disclosure or misappropriation of any of the Confidential Information in violation of this agreement may cause Disclosing Party irreparable harm, the amount of which may be difficult to ascertain, and therefore agrees that Disclosing Party shall have the right to apply to a court of competent jurisdiction for specific performance and/or an order restraining and enjoining any such further disclosure or breach and for such other relief as Disclosing Party shall deem appropriate. Such right of Disclosing Party is to be in addition to the remedies otherwise available to Disclosing Party at law or in equity. Recipient expressly waives the defense that a remedy in damages will be adequate and any requirement in an action for specific performance or injunction for the posting of a bond by Disclosing Party.
              </p>
            </div>

            {/* Section 10: Survival */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">10. <strong>Survival.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                This Agreement shall continue in full force and effect for two years.
              </p>
            </div>

            {/* Section 11: Successors and Assigns */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">11. <strong>Successors and Assigns.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                This Agreement and each party's obligations hereunder shall be binding on the representatives, assigns, and successors of such party and shall inure to the benefit of the assigns and successors of such party; provided, however, that the rights and obligations of Recipient hereunder are not assignable.
              </p>
            </div>

            {/* Section 12: Governing Law */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">12. <strong>Governing Law.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                This Agreement shall be governed by and constructed in accordance with the laws of the State of Texas, without regard to conflict of law principles.
              </p>
            </div>


            {/* Section 13: Counterparts and Right */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">13. <strong>Counterparts and Right.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                This Agreement may be signed in counterparts, which together shall constitute one agreement. The person signing on behalf of Recipient represents that he or she has the right and power to execute this Agreement.
              </p>
            </div>

            {/* Section 14: Entire Agreement */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">14. <strong>Entire Agreement.</strong></h3>
              <p className="text-gray-700 leading-relaxed">
                This Agreement expresses the full and complete understanding of the parties with respect to the subject matter hereof and supersedes all prior or contemporaneous proposals, agreements, representations and understandings, whether written or oral, with respect to the subject matter. This Agreement is not, however, to limit any rights that Disclosing Party may have under trade secret, copyright, patent or other laws that may be available to Disclosing Party. This Agreement may not be amended or modified except in writing signed by each of the parties to the Agreement. This Agreement shall be construed as to its fair meaning and not strictly for or against either party. The headings hereof are descriptive only and not to be construed in interpreting the provisions hereof.
              </p>
            </div>

            {/* Signatures Section */}
            <div className="mt-12 pt-8 border-t-2 border-gray-400">
              {/* Company Names */}
              <div className="mb-8">
                <p className="text-gray-700 font-bold text-lg mb-4"><strong>SNQ AFFILIATES LLC</strong></p>
                <p className="text-gray-700 font-medium">("Disclosing Party")</p>
              </div>

              {/* Signature Blocks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                {/* Disclosing Party Column */}
                <div className="space-y-6">
                  <div className="space-y-3">
                    <p className="text-gray-700 font-medium">Lee Stauss, Member</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      {buyerSignature ? (
                        <div className="border-2 border-gray-300 rounded-lg p-2 bg-white h-24">
                          <img src={buyerSignature} alt="Disclosing Party Signature" className="w-full h-20 object-contain" />
                        </div>
                      ) : (
                        <div className="border-2 border-gray-300 rounded-lg bg-white h-24">
                          <SignatureCanvas
                            ref={buyerSignatureRef}
                            canvasProps={{
                              className: 'w-full h-20'
                            }}
                            backgroundColor="white"
                          />
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        {!buyerSignature && (
                          <>
                            <button
                              onClick={() => saveSignature(buyerSignatureRef, setBuyerSignature)}
                              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => clearSignature(buyerSignatureRef)}
                              className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                        {buyerSignature && (
                          <button
                            onClick={() => setBuyerSignature(null)}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Disclosing Party Contact Info */}
                  <div className="mt-8 space-y-3">
                    <p className="text-sm text-gray-600">By: Lee Stauss</p>
                    <p className="text-sm text-gray-600">Authorized Representative</p>
                    <p className="text-gray-700 text-sm">Address: 2501 NE 23rd St, Suite A445</p>
                    <p className="text-gray-700 text-sm">Oklahoma City, OK 73111</p>
                    {!buyerSignature && (
                      <p className="text-xs text-gray-500 mt-1">
                        Note: Lee Stauss's signature will be automatically loaded if available
                      </p>
                    )}
                  </div>
                </div>

                {/* Receiving Party Column */}
                <div className="space-y-6">
                  <div className="space-y-3">
                    <p className="text-gray-700 font-medium">("Recipient")</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      {recipientSignature ? (
                        <div className="border-2 border-gray-300 rounded-lg p-2 bg-white h-24">
                          <img src={recipientSignature} alt="Receiving Party Signature" className="w-full h-20 object-contain" />
                        </div>
                      ) : (
                        <div className="border-2 border-gray-300 rounded-lg bg-white h-24">
                          <SignatureCanvas
                            ref={recipientSignatureRef}
                            canvasProps={{
                              className: 'w-full h-20'
                            }}
                            backgroundColor="white"
                          />
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        {!recipientSignature && (
                          <>
                            <button
                              onClick={() => saveSignature(recipientSignatureRef, setRecipientSignature)}
                              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => clearSignature(recipientSignatureRef)}
                              className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                        {recipientSignature && (
                          <button
                            onClick={() => setRecipientSignature(null)}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Receiving Party Contact Info */}
                  <div className="mt-8 space-y-3">
                    <p className="text-sm text-gray-600">Recipient Company: 
                      <input 
                        type="text" 
                        value={recipientDetails.company}
                        onChange={isViewOnly ? undefined : (e) => handleRecipientDetailsChange('company', e.target.value)}
                        readOnly={isViewOnly}
                        className={`border-b border-gray-400 px-2 py-1 text-gray-700 text-sm ml-2 ${isViewOnly ? 'bg-gray-100' : 'bg-yellow-200 focus:outline-none focus:border-blue-500'}`}
                        placeholder={isViewOnly ? "" : "Enter recipient company name"}
                      />
                    </p>
                    <p className="text-sm text-gray-600">By: 
                      <input 
                        type="text" 
                        value={recipientDetails.name}
                        onChange={isViewOnly ? undefined : (e) => handleRecipientDetailsChange('name', e.target.value)}
                        readOnly={isViewOnly}
                        className={`border-b border-gray-400 px-2 py-1 text-gray-700 text-sm ml-2 ${isViewOnly ? 'bg-gray-100' : 'bg-yellow-200 focus:outline-none focus:border-blue-500'}`}
                        placeholder={isViewOnly ? "" : "Enter your name"}
                      />
                    </p>
                    <p className="text-sm text-gray-600">Authorized Representative</p>
                    <p className="text-gray-700 text-sm">Address: 
                      <input 
                        type="text" 
                        value={recipientDetails.address}
                        onChange={isViewOnly ? undefined : (e) => handleRecipientDetailsChange('address', e.target.value)}
                        readOnly={isViewOnly}
                        className={`border-b border-gray-400 px-2 py-1 text-gray-700 text-sm ml-2 flex-1 ${isViewOnly ? 'bg-gray-100' : 'bg-yellow-200 focus:outline-none focus:border-blue-500'}`}
                        placeholder={isViewOnly ? "" : "Enter your address"}
                      />
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Section - Always show */}
            <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-blue-900">Submit NDA</h3>
                </div>
                {!isViewOnly && (
                  <>
                    <p className="text-blue-800 text-sm mb-4">
                      Once both parties have signed, the executed NDA will be emailed to you and you will be granted access to the deal information.
                    </p>
                    <button
                      onClick={handleSubmitNDA}
                      disabled={isSubmitting || !buyerSignature || !recipientSignature || !recipientDetails.company || !recipientDetails.email}
                      className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Signed NDA'}
                    </button>
                    {submitMessage && (
                      <p className={`mt-3 text-sm ${submitMessage.includes('successfully') ? 'text-green-600' : 'text-red-600'}`}>
                        {submitMessage}
                      </p>
                    )}
                  </>
                )}
                {isViewOnly && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 text-sm font-medium">
                      ✅ This NDA has been executed and is legally binding.
                    </p>
                    <p className="text-green-700 text-sm mt-2">
                      You can use the Print/PDF button above to save or print this executed NDA.
                    </p>
                  </div>
                )}
              </div>
          </div>

          {/* Footer Note */}
          <div className="mt-8 pt-4 border-t border-gray-300">
            <p className="text-xs text-gray-500 text-center">
              This Non-Disclosure Agreement is legally binding. Please review all terms carefully before signing.
            </p>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 print:hidden">
          <div className="relative max-w-4xl max-h-full p-4">
            <button
              onClick={closeImageModal}
              className="absolute top-2 right-2 text-white hover:text-gray-300 transition-colors z-10"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src="/src/assets/family.jpeg" 
              alt="Lee Stauss - Full Size" 
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-pointer"
              onClick={closeImageModal}
            />
          </div>
        </div>
      )}
    </div>
  );
};

